import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type IncidentStatus = 'open' | 'under_review' | 'closed' | 'cancelled';
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface IncidentDto {
  id: string;
  organisation_id: string;
  alarm_id: string | null;
  affected_user_id: string | null;
  affected_user_display_name: string | null;
  title: string;
  description: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  tags: string[];
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_by_display_name: string | null;
  created_at: string;
  updated_at: string | null;
  closed_at: string | null;
  closed_by: string | null;
}

export interface IncidentNoteDto {
  id: string;
  incident_id: string;
  actor_id: string | null;
  actor_display_name: string | null;
  body: string;
  entry_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface CreateIncidentInput {
  title: string;
  description: string;
  severity?: IncidentSeverity;
  alarm_id?: string | null;
  affected_user_id?: string | null;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface UpdateIncidentInput {
  id: string;
  title?: string;
  description?: string;
  severity?: IncidentSeverity;
  status?: IncidentStatus;
  tags?: string[];
  affected_user_id?: string | null;
}

export function useIncidentsList(
  filters: {
    status?: IncidentStatus;
    severity?: IncidentSeverity;
    affected_user_id?: string;
  } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: ['incidents', 'list', filters],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.status) qs.set('status', filters.status);
      if (filters.severity) qs.set('severity', filters.severity);
      if (filters.affected_user_id) qs.set('affected_user_id', filters.affected_user_id);
      qs.set('limit', '200');
      return api<{ incidents: IncidentDto[] }>(`/incidents?${qs.toString()}`);
    },
    staleTime: 15_000,
  });
}

export function useIncident(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['incidents', id],
    queryFn: () => api<{ incident: IncidentDto; notes: IncidentNoteDto[] }>(`/incidents/${id}`),
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

export function useCreateIncident() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIncidentInput) =>
      api<{ incident: IncidentDto }>('/incidents', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents'] }),
  });
}

export function useUpdateIncident() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateIncidentInput) =>
      api<{ incident: IncidentDto }>(`/incidents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents'] }),
  });
}

export function useAddIncidentNote(incidentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api<{ note: IncidentNoteDto }>(`/incidents/${incidentId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents', incidentId] }),
  });
}
